/**
 * ExpenseReviewModal — the expense approval queue, in its own window (Matt, 2026-10-28: pairing it
 * with Time Review "is a little too much on one view").
 *
 * Same people, same rule as before: whoever reviews a person's time reviews their expenses
 * (time_review_assignments, enforced server-side by api/expense-reports.js). Approval needs a phase,
 * since it decides which invoice the expense lands on.
 *
 * WHERE IT GOES (Matt, 2026-10-28: "do I get to see its posting status?"): approving a PAY-ME-BACK
 * expense writes it into Core at once; a COMPANY-PAID one reaches Core through Bookkeeping's check for
 * the charge, never from here (docs/EXPENSE_REPORTS.md). Every approved row shows which, with the
 * Core entry or the reason it failed and a Retry, like Time Review's Posted / Post Failed. The
 * Decided tab keeps the last 14 days (list scope 'decided').
 *
 * KEYBOARD: ↑/↓ move, A approve, R send back, 1-9 pick that phase, P open the phase list, O open the
 * receipt, T retry a failed Core post, ←/→ previous/next person, Tab switch To review / Decided, Esc
 * close. Keys pause while a confirm dialog is up or a field has focus (Esc/Enter there return).
 *
 * Opens on the `open-expense-review` CustomEvent; `detail.memberId` starts it on one person.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './timereview.css'
import { listExpenseReports, decideExpenseReport, retryExpenseCore, isMileage } from '../services/expenseReports.js'
import { getEntryPhases } from '../services/timecards.js'
import { alertDialog, promptDialog } from './Dialog.jsx'

const money = (v) => v == null ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const day = (d) => d ? new Date(String(d).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 'no date'
const itemName = (c) => String(c || '').replace(/^EX-\s*/, '')
const shortPhase = (n) => { const p = String(n || '').split(' - '); return p.length > 2 ? p.slice(2).join(' - ') : n }
const filerKey = (x) => x.memberId || '__none__'
const first = (x) => String(x.memberName || '').split(' ')[0] || 'the filer'
const payBack = (x) => (x.payor || 'Employee') === 'Employee'

// The phases this expense may go to: those open for time on its project (by project number), or the
// overhead project's for an overhead expense. Same rule Time Review used.
function phaseOptions(x, phases) {
  if (x.expenseType === 'Overhead' || !x.projectName) return phases.filter(p => /overhead/i.test(p.name))
  const num = String(x.projectName).trim().split(/\s+/)[0]
  return phases.filter(p => num && (String(p.name).indexOf(num + '-') === 0 || String(p.name).indexOf(num + ' ') === 0))
}

// Where a decided expense stands. { tone: 'ok'|'wait'|'bad'|'muted', label, detail }
function coreState(x) {
  if (x.status === 'Rejected') return { tone: 'muted', label: 'Sent back', detail: String(x.workflowMemo || '').split(' — ')[0] }
  if (x.status === 'Recorded in BQE Core') return { tone: 'ok', label: 'In Core', detail: 'Recorded by Accounting' }
  if (!payBack(x)) return { tone: 'wait', label: 'Company paid', detail: 'Reaches Core with the card charge in Bookkeeping' }
  const c = x.core
  if (c && c.status === 'written' && c.paidAt) return { tone: 'ok', label: 'Paid back', detail: 'On ' + day(c.paidAt) }
  if (c && c.status === 'written') return { tone: 'ok', label: 'In Core', detail: 'Owed back to ' + first(x) + (c.coreEntryId ? ' · entry ' + String(c.coreEntryId).slice(0, 8) : '') }
  if (c && c.status === 'failed') return { tone: 'bad', label: 'Not in Core', detail: c.error || 'Core refused it', retry: true }
  return { tone: 'wait', label: 'Waiting for Core', detail: 'Not sent yet', retry: true }
}

export default function ExpenseReviewModal() {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState('review')          // 'review' | 'decided'
  const [who, setWho] = useState(null)              // filer key, or null = everyone
  const [list, setList] = useState(null)            // the queue; null = loading
  const [decided, setDecided] = useState(null)      // the last 14 days; null = loading
  const [error, setError] = useState('')
  const [phases, setPhases] = useState([])
  const [picked, setPicked] = useState({})          // expense id -> phase id
  const [busy, setBusy] = useState({})              // expense id -> 'approve' | 'reject' | 'retry'
  const [focusId, setFocusId] = useState(null)      // the keyboard-selected expense
  const [showKbd, setShowKbd] = useState(false)
  const [flash, setFlash] = useState(null)          // { id, text, bad } — the last decision's answer
  const clearFlash = useCallback(() => setFlash(null), [])
  const kb = useRef({})                             // the latest render's key handler
  // ONE AT A TIME (2026-10-28): two approvals for the same person and month write the same Reimb bill
  // in Core, and two at once could lose a line. However fast A is pressed, decisions go in order.
  const queue = useRef(Promise.resolve())

  const load = useCallback(() => {
    setError('')
    listExpenseReports('review')
      .then(d => setList((d && d.reports) || []))
      // A failed load is not "nothing to review".
      .catch(e => { setList([]); setError((e && e.message) || 'Could not load expenses') })
    listExpenseReports('decided').then(d => setDecided((d && d.reports) || [])).catch(() => setDecided([]))
  }, [])

  useEffect(() => {
    function onOpen(e) {
      const d = (e && e.detail) || {}
      setWho(d.memberId || null); setTab('review'); setList(null); setDecided(null)
      setPicked({}); setBusy({}); setFocusId(null); setOpen(true)
    }
    function onTime() { setOpen(false) }   // the other way round: Time Review closes this one
    window.addEventListener('open-expense-review', onOpen)
    window.addEventListener('open-timecard-review', onTime)
    return () => { window.removeEventListener('open-expense-review', onOpen); window.removeEventListener('open-timecard-review', onTime) }
  }, [])

  useEffect(() => {
    if (!open) return undefined
    load()
    getEntryPhases().then(p => setPhases(p || [])).catch(() => {})
    function onKey(e) { const h = kb.current.onKey; if (h) h(e) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey); document.body.style.overflow = ''
      window.dispatchEvent(new Event('time-gate-recheck'))
    }
  }, [open, load])

  const source = tab === 'review' ? list : decided
  const people = useMemo(() => {
    const m = new Map()
    for (const x of source || []) {
      const k = filerKey(x)
      if (!m.has(k)) m.set(k, { key: k, name: x.memberName || (x.memberId ? 'Unknown member' : 'No filer recorded'), items: [] })
      m.get(k).items.push(x)
    }
    return [...m.values()]
  }, [source])

  const shown = people.filter(p => !who || p.key === who)
  const items = shown.flatMap(p => p.items)
  const focused = items.find(x => x.id === focusId) || items[0] || null

  // Keep the selected expense in view as it moves.
  useEffect(() => {
    if (!open || !focused) return
    const el = document.querySelector(`[data-exp-row="${focused.id}"]`)
    if (el) el.scrollIntoView({ block: 'nearest' })
  }, [open, focused && focused.id])   // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null

  const total = items.reduce((a, x) => a + (Number(x.amount) || 0), 0)
  const nextAfter = (x) => { const i = items.findIndex(e => e.id === x.id); const n = items[i + 1] || items[i - 1]; return n ? n.id : null }

  function decide(x, decision) {
    if (busy[x.id]) return
    setBusy(b => ({ ...b, [x.id]: b[x.id] || 'queued' }))
    // An approval moves the selection on at once, so the next A lands on the next expense.
    if (decision === 'approve') setFocusId(nextAfter(x))
    queue.current = queue.current.then(() => decideNow(x, decision)).catch(() => {})
      .finally(() => setBusy(b => (b[x.id] === 'queued' ? (({ [x.id]: _, ...rest }) => rest)(b) : b)))
    return queue.current
  }

  async function decideNow(x, decision) {
    const phaseId = picked[x.id] || (/^rec/.test(x.phaseId || '') ? x.phaseId : '')
    if (decision === 'approve' && !phaseId) {
      await alertDialog({ title: 'Pick the phase first', message: 'Choose the phase on this expense (1-9, or P for the list), then approve. It decides which invoice the expense lands on.' })
      return
    }
    let memo = ''
    if (decision === 'reject') {
      memo = await promptDialog({
        title: 'Send back to ' + (x.memberName || 'the filer') + '?',
        message: 'They get this note on Slack with a link to the expense, and see it on the expense when they open it. Say what needs fixing.',
        placeholder: 'Which site visit? Add the address.', multiline: true, required: true, confirmLabel: 'Send back',
      })
      if (memo === null) return
    }
    setBusy(b => ({ ...b, [x.id]: decision }))
    try {
      const res = await decideExpenseReport(x.id, decision, memo, decision === 'approve' ? phaseId : null)
      const core = res && res.core
      // The decided row, with where it now stands, moves to the Decided tab.
      const row = {
        ...x, ...((res && res.report) || {}), memberName: x.memberName,
        status: decision === 'approve' ? 'Approved' : 'Rejected',
        workflowMemo: memo || '',
        core: core && core.ok && !core.waitsForBank ? { status: 'written', coreEntryId: core.coreEntryId || null }
          : core && core.ok === false ? { status: 'failed', error: core.error } : (x.core || null),
      }
      setFocusId(cur => (cur === x.id ? nextAfter(x) : cur))   // only if the reviewer has not moved on
      setList(l => (l || []).filter(e => e.id !== x.id))
      setDecided(d => [row, ...(d || []).filter(e => e.id !== x.id)])
      setFlash({ id: x.id, text: decision === 'reject' ? `Sent back to ${first(x)}.` : (() => { const st = coreState(row); return `Approved. ${st.label}: ${st.detail}.` })(), bad: !!(core && core.ok === false) })
    } catch (e) {
      await alertDialog({ title: 'Not saved', message: (e && e.message) || 'The expense could not be updated.' })
    } finally {
      setBusy(b => { const n = { ...b }; delete n[x.id]; return n })
    }
  }

  async function retry(x) {
    setBusy(b => ({ ...b, [x.id]: 'retry' }))
    try {
      const res = await retryExpenseCore(x.id, picked[x.id] || null)
      const c = res && res.core
      setDecided(d => (d || []).map(e => e.id !== x.id ? e : { ...e, ...(picked[x.id] ? { phaseId: picked[x.id] } : {}), core: c && c.ok ? { status: 'written', coreEntryId: c.coreEntryId || null } : { status: 'failed', error: (c && c.error) || 'Core refused it' } }))
    } catch (e) {
      setDecided(d => (d || []).map(r => r.id !== x.id ? r : { ...r, core: { status: 'failed', error: (e && e.message) || 'Core refused it' } }))
    } finally {
      setBusy(b => { const n = { ...b }; delete n[x.id]; return n })
    }
  }

  const move = (d) => {
    if (!items.length) return
    const i = Math.max(0, items.findIndex(x => x.id === (focused && focused.id)))
    setFocusId(items[Math.min(items.length - 1, Math.max(0, i + d))].id)
  }
  const switchPerson = (d) => {
    const keys = [null, ...people.map(p => p.key)]
    const i = Math.max(0, keys.indexOf(who))
    setWho(keys[(i + d + keys.length) % keys.length]); setFocusId(null)
  }
  const switchTab = (t) => { setTab(t); setWho(null); setFocusId(null) }

  kb.current.onKey = (e) => {
    const ae = document.activeElement
    // A confirm/prompt on top (it takes focus) owns the keys. Not any role="dialog": the lockout
    // cards behind this window carry it too, and matching them made every key dead.
    const dlg = ae && ae.closest ? ae.closest('[role="dialog"]') : null
    if (dlg && !dlg.closest('.tr-modal')) return
    if (ae && /^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName)) {
      // Esc, or Enter in the phase list, hands the keys back to the list.
      if (e.key === 'Escape' || (e.key === 'Enter' && ae.tagName === 'SELECT')) { e.preventDefault(); ae.blur() }
      return
    }
    if (e.metaKey || e.ctrlKey || e.altKey) return
    const k = e.key, low = k.toLowerCase()
    if (k === 'Escape') { e.preventDefault(); if (showKbd) setShowKbd(false); else setOpen(false); return }
    if (k === 'Tab') { e.preventDefault(); switchTab(tab === 'review' ? 'decided' : 'review'); return }
    if (k === 'ArrowDown' || k === 'ArrowUp') { e.preventDefault(); move(k === 'ArrowDown' ? 1 : -1); return }
    if (k === 'ArrowLeft' || k === 'ArrowRight') { if (people.length > 1) { e.preventDefault(); switchPerson(k === 'ArrowRight' ? 1 : -1) } return }
    if (!focused || busy[focused.id]) return
    if (low === 'o') { const rc = (focused.receipts || [])[0]; if (rc) { e.preventDefault(); window.open(rc.url, '_blank', 'noopener') } return }
    if (tab === 'decided') {
      if (/^[1-9]$/.test(k) && !/^rec/.test(focused.phaseId || '')) {
        const p = phaseOptions(focused, phases)[Number(k) - 1]
        if (p) { e.preventDefault(); setPicked(m => ({ ...m, [focused.id]: p.id })) }
        return
      }
      if (low === 't' && coreState(focused).retry && payBack(focused) && focused.status === 'Approved') { e.preventDefault(); retry(focused) }
      return
    }
    if (low === 'a') { e.preventDefault(); decide(focused, 'approve') }
    else if (low === 'r') { e.preventDefault(); decide(focused, 'reject') }
    else if (low === 'p') {
      e.preventDefault()
      const sel = document.querySelector(`[data-exp-row="${focused.id}"] select`)
      if (sel) { sel.focus(); try { if (sel.showPicker) sel.showPicker() } catch (_) { /* not allowed everywhere */ } }
    }
    else if (/^[1-9]$/.test(k)) {
      const p = phaseOptions(focused, phases)[Number(k) - 1]
      if (p) { e.preventDefault(); setPicked(m => ({ ...m, [focused.id]: p.id })) }
    }
  }

  const reviewCount = (list || []).length
  const failedCount = (decided || []).filter(x => x.status === 'Approved' && coreState(x).tone === 'bad').length

  return (
    <div className="tr-overlay">
      <div className="tr-modal" style={{ maxWidth: 880, position: 'relative' }}>
        {flash && <Flash flash={flash} onDone={clearFlash} />}
        <div className="tr-modal-bar">
          <span className="tr-modal-title">Expense Review</span>
          <div className="tr-bar-right">
            <button className="tr-kbd-btn" title="Keyboard navigation" onClick={() => setShowKbd(v => !v)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8" />
              </svg>
              Keyboard
            </button>
            <button className="tr-modal-close" title="Close (Esc)" onClick={() => setOpen(false)}>&times;</button>
          </div>
          {showKbd && (
            <>
              <div className="tr-kbd-backdrop" onClick={() => setShowKbd(false)} />
              <div className="tr-kbd-pop" role="note" aria-label="Keyboard navigation">
                <div className="tr-kbd-pop-title">Keyboard navigation</div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>↑</kbd><kbd>↓</kbd></span><span>Move between expenses</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>A</kbd></span><span>Approve</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>R</kbd></span><span>Send back</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>1</kbd><kbd>9</kbd></span><span>Pick that phase</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>P</kbd></span><span>Open the phase list (Enter sets it)</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>O</kbd></span><span>Open the receipt</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>T</kbd></span><span>Retry a failed Core post (Decided)</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>←</kbd><kbd>→</kbd></span><span>Previous / next person</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>Tab</kbd></span><span>To review / Decided</span></div>
                <div className="tr-kbd-row"><span className="tr-keys"><kbd>Esc</kbd></span><span>Close</span></div>
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 2, padding: '0 18px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <button type="button" style={tabBtn(tab === 'review')} onClick={() => switchTab('review')}>
            To review{list ? ' · ' + reviewCount : ''}
          </button>
          <button type="button" style={tabBtn(tab === 'decided')} onClick={() => switchTab('decided')}>
            Decided, last 14 days{decided ? ' · ' + decided.length : ''}
            {failedCount > 0 && <span style={{ marginLeft: 6, color: '#c0392b' }}>· {failedCount} not in Core</span>}
          </button>
        </div>
        <div className="tr-modal-scroll" style={{ padding: '14px 18px 22px', fontFamily: '"DM Sans", sans-serif', color: 'var(--aj-text)' }}>
          {source === null ? (
            <div style={S.muted}>Loading expenses…</div>
          ) : tab === 'review' && error ? (
            <div style={{ fontSize: 13, color: '#c0392b' }}>Expenses could not be loaded: {error}</div>
          ) : !people.length ? (
            <div style={S.muted}>{tab === 'review' ? 'Nothing to review. Every expense from the people you review is decided.' : 'Nothing decided in the last 14 days.'}</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--aj-text-mid)', marginBottom: 10 }}>
                <span style={S.mono}>{items.length}</span> expense{items.length === 1 ? '' : 's'}{tab === 'review' ? ' to review' : ' decided'} · <span style={S.mono}>{money(total)}</span>
              </div>
              {people.length > 1 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                  <button type="button" style={chip(!who)} onClick={() => { setWho(null); setFocusId(null) }}>Everyone · {people.reduce((n, p) => n + p.items.length, 0)}</button>
                  {people.map(p => (
                    <button key={p.key} type="button" style={chip(who === p.key)} onClick={() => { setWho(p.key); setFocusId(null) }}>{p.name} · {p.items.length}</button>
                  ))}
                </div>
              )}
              {shown.map(p => (
                <div key={p.key} style={{ marginBottom: 18 }}>
                  <div style={S.personHead}>
                    <span>{p.name}</span>
                    <span style={{ ...S.mono, fontSize: 12, color: 'var(--aj-text-muted)', fontWeight: 400 }}>
                      {p.items.length} · {money(p.items.reduce((a, x) => a + (Number(x.amount) || 0), 0))}
                    </span>
                  </div>
                  {p.items.map(x => (
                    <ExpenseRow key={x.id} x={x} mode={tab} phases={phases} picked={picked[x.id]}
                      focused={!!focused && focused.id === x.id} onFocusRow={() => setFocusId(x.id)}
                      onPick={v => setPicked(m => ({ ...m, [x.id]: v }))} busy={busy[x.id]} onDecide={decide} onRetry={retry} />
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// The answer to a decision, in a plain sentence, folding away after a moment (a Core failure stays
// longer). The row itself has already moved to Decided, where its status stays.
function Flash({ flash, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, flash.bad ? 7000 : 3000); return () => clearTimeout(t) }, [flash, onDone])
  return (
    // Floats over the bottom of the window (Matt, 2026-10-28: the in-list message pushed every row down
    // and they jumped back up when it went, mid-review). Never takes space in the list, never the pointer.
    <div role="status" style={{ position: 'absolute', left: '50%', bottom: 16, transform: 'translateX(-50%)', zIndex: 5,
      maxWidth: 'calc(100% - 48px)', fontSize: 13, padding: '8px 14px', borderRadius: 8, pointerEvents: 'none',
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)', border: '0.5px solid var(--aj-border-mid)',
      background: flash.bad ? '#fdecea' : 'var(--aj-bg-elevated)', color: flash.bad ? '#c0392b' : 'var(--aj-text-mid)' }}>
      {flash.text}{flash.bad ? ' It is in the Decided tab with a Retry.' : ''}
    </div>
  )
}

function ExpenseRow({ x, mode, phases, picked, onPick, busy, onDecide, onRetry, focused, onFocusRow }) {
  const mi = isMileage(x.itemCode)
  const opts = phaseOptions(x, phases)
  const cur = picked != null ? picked : (/^rec/.test(x.phaseId || '') ? x.phaseId : '')
  const curName = (phases.find(p => p.id === cur) || {}).name
  const where = [x.projectName || (x.expenseType === 'Project' ? '(No project)' : 'Overhead'), x.payee, x.memo].filter(Boolean).join(' · ')
  const st = mode === 'decided' ? coreState(x) : null
  return (
    <div data-exp-row={x.id} onMouseDown={onFocusRow} style={{ ...S.row, ...(focused ? S.rowFocused : null) }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{day(x.date)} · {itemName(x.itemCode)}</div>
        <div style={{ fontSize: 12.5, color: 'var(--aj-text-mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={where}>{where}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
          <span style={S.pill} title={payBack(x) ? 'The filer paid and is owed this back. Approving writes it into Core.' : 'Paid on a company card or account. Reaches Core with the charge in Bookkeeping.'}>{payBack(x) ? 'Pay back' : 'Company paid'}</span>
          {(x.receipts || []).map((rc, i) => (
            <a key={i} href={rc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: 'var(--aj-accent-blue)' }}>{x.receipts.length > 1 ? 'Receipt ' + (i + 1) : 'Receipt'}</a>
          ))}
          {!(x.receipts || []).length && !mi && <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>No receipt</span>}
        </div>
        {(mode === 'review' || (st && st.retry && payBack(x) && x.status === 'Approved' && !/^rec/.test(x.phaseId || ''))) ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
            {mode === 'decided' && <span style={{ ...S.status, ...TONE.bad }}>{st.label}</span>}
            <span style={{ fontSize: 12, color: cur ? 'var(--aj-text-muted)' : '#c0392b' }}>{cur ? 'Phase' : 'Pick a phase'}</span>
            <select value={cur} onChange={e => onPick(e.target.value)} style={S.select}>
              <option value="">{opts.length ? 'Choose…' : 'No phase open for time on this project'}</option>
              {opts.map((p, i) => <option key={p.id} value={p.id}>{i < 9 ? (i + 1) + '. ' : ''}{shortPhase(p.name)}</option>)}
              {cur && !opts.some(p => p.id === cur) && <option value={cur}>(a closed phase)</option>}
            </select>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 6, flexWrap: 'wrap' }}>
            <span style={{ ...S.status, ...TONE[st.tone] }}>{st.label}</span>
            <span style={{ fontSize: 12, color: st.tone === 'bad' ? '#c0392b' : 'var(--aj-text-muted)' }}>{st.detail}</span>
            {curName && <span style={{ fontSize: 12, color: 'var(--aj-text-muted)' }}>· {shortPhase(curName)}</span>}
          </div>
        )}
      </div>
      <div style={{ ...S.mono, fontSize: 13, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {mi && x.units ? x.units + ' mi · ' : ''}{x.amount == null ? 'no rate' : money(x.amount)}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        {mode === 'review' ? (
          <>
            <button type="button" style={btn(true, busy)} disabled={!!busy} onClick={() => onDecide(x, 'approve')}>{busy === 'approve' ? 'Approving…' : busy === 'queued' ? 'In line…' : 'Approve'}</button>
            <button type="button" style={btn(false, busy)} disabled={!!busy} onClick={() => onDecide(x, 'reject')}>{busy === 'reject' ? 'Sending…' : 'Send back'}</button>
          </>
        ) : st.retry && payBack(x) && x.status === 'Approved' ? (
          <button type="button" style={btn(true, busy)} disabled={!!busy} onClick={() => onRetry(x)}>{busy === 'retry' ? 'Sending…' : 'Retry post'}</button>
        ) : null}
      </div>
    </div>
  )
}

const TONE = {
  ok: { color: '#1e7a4f', borderColor: 'rgba(30,122,79,0.45)' },
  wait: { color: 'var(--aj-text-mid)', borderColor: 'var(--aj-border-mid)' },
  bad: { color: '#c0392b', borderColor: 'rgba(192,57,43,0.5)' },
  muted: { color: 'var(--aj-text-muted)', borderColor: 'var(--aj-border)' },
}

const btn = (primary, busy) => ({
  font: 'inherit', fontSize: 12, fontWeight: 600, padding: '6px 12px', borderRadius: 4, cursor: busy ? 'default' : 'pointer',
  border: '0.5px solid ' + (primary ? '#07272D' : 'var(--aj-border-mid)'), opacity: busy ? 0.5 : 1,
  background: primary ? '#07272D' : 'transparent', color: primary ? '#fff' : 'var(--aj-text-mid)', whiteSpace: 'nowrap',
})

const tabBtn = (on) => ({
  font: 'inherit', fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: on ? 600 : 400, padding: '10px 12px', cursor: 'pointer',
  background: 'none', border: 'none', borderBottom: '2px solid ' + (on ? 'var(--aj-text)' : 'transparent'),
  color: on ? 'var(--aj-text)' : 'var(--aj-text-muted)', marginBottom: -0.5,
})

const chip = (on) => ({
  font: 'inherit', fontSize: 12, padding: '4px 11px', borderRadius: 20, cursor: 'pointer',
  border: '0.5px solid ' + (on ? 'var(--aj-text)' : 'var(--aj-border-mid)'),
  background: on ? 'var(--aj-text)' : 'transparent', color: on ? 'var(--aj-bg-elevated)' : 'var(--aj-text-mid)',
})

const S = {
  muted: { fontSize: 13, color: 'var(--aj-text-muted)', padding: '18px 0' },
  mono: { fontFamily: '"DM Mono", monospace' },
  personHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 14, fontWeight: 600, paddingBottom: 6, borderBottom: '0.5px solid var(--aj-border-mid)' },
  row: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto auto', gap: 12, alignItems: 'center', padding: '10px 8px', margin: '0 -8px', borderBottom: '0.5px solid var(--aj-border)', cursor: 'default' },
  rowFocused: { background: 'var(--aj-bg-subtle)', boxShadow: 'inset 3px 0 0 var(--aj-accent-blue)', borderRadius: 4 },
  pill: { fontSize: 10.5, fontWeight: 600, padding: '1px 8px', borderRadius: 20, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text-mid)' },
  status: { fontSize: 11, fontWeight: 600, padding: '1px 8px', borderRadius: 20, border: '0.5px solid' },
  select: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, padding: '3px 5px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'transparent', color: 'inherit', maxWidth: 300 },
}
